// VA-Massenimport: Job-Übersicht (05.08.2026)
//
// Volle Breite, keine Karte in den Einstellungen — dieselbe Begründung wie bei
// MailingListPage: die Tabelle braucht Platz (Zeilenfortschritt, Fehlerliste,
// vier Aktionsknöpfe), und ein Klappwerk wäre hier mehr Bauwerk als Nutzen.
//
// Polling übernimmt useEventImportJobs() selbst (siehe event-service.ts) —
// diese Seite ruft nur den Hook auf und rendert, was zurückkommt.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp, Play, Square, Undo2, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { useToast } from '@/hooks/use-toast';
import { useMe } from '@/hooks/usePermissions';
import { ApiError } from '@/lib/api-client';
import {
  useEventImportJobs,
  useStartEventImportJob,
  useStopEventImportJob,
  useRollbackEventImportJob,
  useDeleteEventImportJob,
} from '@/services/event-service';
import { Role, type ImportJobStatus, type ImportJobStatusFile } from '@/types';
import { EventImportModal } from './EventImportModal';

const BADGE_VARIANT: Record<ImportJobStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  pending: 'outline',
  running: 'default',
  stopped: 'secondary',
  completed: 'secondary',
  failed: 'destructive',
  rolled_back: 'outline',
};

export function EventImportJobsPage() {
  const { t } = useTranslation('settings');
  const { toast } = useToast();

  const { data: me } = useMe();
  const canManage = me?.role === Role.Zentral || me?.role === Role.Admin;

  const { data: jobs = [], isLoading } = useEventImportJobs();
  const startMutation = useStartEventImportJob();
  const stopMutation = useStopEventImportJob();
  const rollbackMutation = useRollbackEventImportJob();
  const deleteMutation = useDeleteEventImportJob();

  const [uploadOpen, setUploadOpen] = useState(false);
  const [expandedErrors, setExpandedErrors] = useState<string | null>(null);
  const [pendingRollback, setPendingRollback] = useState<ImportJobStatusFile | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ImportJobStatusFile | null>(null);

  const anyRunning = jobs.some((j) => j.status === 'running');

  const handleStart = async (jobId: string) => {
    try {
      await startMutation.mutateAsync(jobId);
    } catch (err) {
      toast({
        variant: 'destructive',
        description: err instanceof ApiError ? err.message : t('eventImport.startFailed'),
      });
    }
  };

  const handleStop = async (jobId: string) => {
    try {
      await stopMutation.mutateAsync(jobId);
    } catch (err) {
      toast({
        variant: 'destructive',
        description: err instanceof ApiError ? err.message : t('eventImport.stopFailed'),
      });
    }
  };

  const handleRollback = async () => {
    const job = pendingRollback;
    if (!job) return;
    setPendingRollback(null);
    try {
      const result = await rollbackMutation.mutateAsync(job.jobId);
      if (result.failed.length > 0) {
        toast({
          variant: 'destructive',
          description: t('eventImport.rollbackPartial', {
            removed: result.removed.length,
            failed: result.failed.length,
          }),
        });
      } else {
        toast({ description: t('eventImport.rollbackSuccess', { count: result.removed.length }) });
      }
    } catch (err) {
      toast({
        variant: 'destructive',
        description: err instanceof ApiError ? err.message : t('eventImport.rollbackFailed'),
      });
    }
  };

  const handleDelete = async () => {
    const job = pendingDelete;
    if (!job) return;
    setPendingDelete(null);
    try {
      await deleteMutation.mutateAsync(job.jobId);
    } catch (err) {
      toast({
        variant: 'destructive',
        description: err instanceof ApiError ? err.message : t('eventImport.deleteFailed'),
      });
    }
  };

  if (!canManage) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground">{t('eventImport.accessDenied')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/settings"><ArrowLeft className="h-4 w-4" /></Link>
          </Button>
          <div>
            <h1 className="text-lg font-semibold">{t('eventImport.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('eventImport.description')}</p>
          </div>
        </div>
        <Button className="gap-2" onClick={() => setUploadOpen(true)} disabled={anyRunning}>
          <Upload className="h-4 w-4" />
          {t('eventImport.newImport')}
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">{t('eventImport.loading')}</p>
      ) : jobs.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('eventImport.empty')}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('eventImport.table.filename')}</TableHead>
              <TableHead>{t('eventImport.table.status')}</TableHead>
              <TableHead>{t('eventImport.table.progress')}</TableHead>
              <TableHead>{t('eventImport.table.targetStatus')}</TableHead>
              <TableHead className="text-right">{t('eventImport.table.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {jobs.map((job) => (
              <JobRow
                key={job.jobId}
                job={job}
                expanded={expandedErrors === job.jobId}
                onToggleErrors={() =>
                  setExpandedErrors(expandedErrors === job.jobId ? null : job.jobId)
                }
                onStart={() => void handleStart(job.jobId)}
                onStop={() => void handleStop(job.jobId)}
                onRollback={() => setPendingRollback(job)}
                onDelete={() => setPendingDelete(job)}
              />
            ))}
          </TableBody>
        </Table>
      )}

      <EventImportModal
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onUploaded={() => {
          toast({ description: t('eventImport.uploadSuccess') });
        }}
      />

      <ConfirmationDialog
        open={pendingRollback !== null}
        onOpenChange={(open) => !open && setPendingRollback(null)}
        title={t('eventImport.rollbackDialog.title')}
        description={t('eventImport.rollbackDialog.description', {
          count: pendingRollback?.created.length ?? 0,
        })}
        confirmLabel={t('eventImport.rollbackDialog.confirm')}
        variant="destructive"
        onConfirm={() => void handleRollback()}
      />

      <ConfirmationDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={t('eventImport.deleteDialog.title')}
        description={t('eventImport.deleteDialog.description', { filename: pendingDelete?.filename ?? '' })}
        confirmLabel={t('eventImport.deleteDialog.confirm')}
        variant="destructive"
        onConfirm={() => void handleDelete()}
      />
    </div>
  );
}

function JobRow({
  job,
  expanded,
  onToggleErrors,
  onStart,
  onStop,
  onRollback,
  onDelete,
}: {
  job: ImportJobStatusFile;
  expanded: boolean;
  onToggleErrors: () => void;
  onStart: () => void;
  onStop: () => void;
  onRollback: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation('settings');

  return (
    <>
      <TableRow>
        <TableCell className="font-medium">{job.filename}</TableCell>
        <TableCell>
          <Badge variant={BADGE_VARIANT[job.status]}>{t(`eventImport.status.${job.status}`)}</Badge>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            <span className="text-sm">
              {job.processed}/{job.total}
            </span>
            {job.errors.length > 0 && (
              <Button variant="ghost" size="sm" className="h-6 gap-1 px-2 text-amber-700" onClick={onToggleErrors}>
                {t('eventImport.table.errorsCount', { count: job.errors.length })}
                {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </Button>
            )}
          </div>
        </TableCell>
        <TableCell>
          {job.targetStatus === 'approved'
            ? t('eventImport.targetStatusApproved')
            : t('eventImport.targetStatusInitial')}
        </TableCell>
        <TableCell className="text-right">
          <div className="flex justify-end gap-1">
            {job.status === 'pending' && (
              <Button variant="ghost" size="icon" title={t('eventImport.actions.start')} onClick={onStart}>
                <Play className="h-4 w-4" />
              </Button>
            )}
            {job.status === 'running' && (
              <Button variant="ghost" size="icon" title={t('eventImport.actions.stop')} onClick={onStop}>
                <Square className="h-4 w-4" />
              </Button>
            )}
            {job.created.length > 0 && job.status !== 'running' && (
              <Button variant="ghost" size="icon" title={t('eventImport.actions.rollback')} onClick={onRollback}>
                <Undo2 className="h-4 w-4" />
              </Button>
            )}
            {job.status !== 'running' && (
              <Button variant="ghost" size="icon" title={t('eventImport.actions.delete')} onClick={onDelete}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </TableCell>
      </TableRow>
      {expanded && job.errors.length > 0 && (
        <TableRow>
          <TableCell colSpan={5} className="bg-amber-50">
            <ul className="list-inside list-disc text-xs text-amber-800">
              {job.errors.map((rowError) => (
                <li key={`${rowError.row}-${rowError.reason}`}>
                  {t('eventImport.table.row', { row: rowError.row })}: {rowError.reason}
                </li>
              ))}
            </ul>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
